<script>
  import Section from '../components/Section.svelte';
  import Panel from '../components/Panel.svelte';
  import Alert from '../components/Alert.svelte';
  import Button from '../components/Button.svelte';
  import DataTable from '../components/DataTable.svelte';
  import Async from '../components/Async.svelte';
  import HelpDrawer from '../components/HelpDrawer.svelte';
  import { getPartners, getDatasets, getConfig, publish } from '../lib/api.js';
  import { datasetLabel, datasetGroups } from '../lib/slices.js';
  import { HELP } from '../lib/help.js';

  // Re-fetch the registry after a republish so the refreshed publishedAt shows.
  let reloadId = $state(0);
  const partners = $derived.by(() => { reloadId; return getPartners(); });

  let busy = $state(null); // webId currently republishing
  let error = $state(null);
  let notice = $state(null);

  // Names for the dataset ids a grant holds; a failure leaves the ids.
  let datasets = $state([]);
  getDatasets().then((r) => { datasets = r.datasets; }).catch(() => {});

  // "Send an updated copy" = republish the partner's granted datasets with the
  // current aggregates. Republish is a governance act (the ingest job never
  // touches shared containers), so only then does a new period reach the partner.
  // The registry stores datasets as an array; publish() wants the flag map.
  async function updateCubes(p) {
    busy = p.webId; error = null; notice = null;
    try {
      const granted = Object.fromEntries((p.outbound.datasets ?? []).map((id) => [id, true]));
      const res = await publish({ webId: p.webId, datasets: granted });
      notice = res.ok
        ? `Sent ${p.label} an updated copy of ${res.outbound?.slices.length ?? 0} counts.`
        : `Sent ${p.label} an update, but some of it failed — check the cubes under Advanced.`;
      reloadId += 1;
    } catch (e) {
      error = e.message;
    } finally {
      busy = null;
    }
  }

  // "Change what you share": the datasets with counts, ticked for what the
  // partner gets now. Saving sends the ticked ones and withdraws the rest,
  // which deletes their copy.
  let groups = $state([]);
  getConfig('aggregations').then((aggregations) => getDatasets()
    .then(({ datasets }) => { groups = datasetGroups(aggregations, datasets); })).catch(() => {});
  let editing = $state(null); // webId whose sharing is being changed
  let choice = $state({});
  function edit(p) {
    editing = p.webId; error = null; notice = null;
    choice = Object.fromEntries(groups.map((g) => [g.id, (p.outbound.datasets ?? []).includes(g.id)]));
  }
  const changes = (p) => {
    const had = new Set(p.outbound.datasets ?? []);
    return { added: groups.filter((g) => choice[g.id] && !had.has(g.id)),
      withdrawn: groups.filter((g) => !choice[g.id] && had.has(g.id)) };
  };
  const names = (list) => list.map((g) => g.label).join(', ');
  async function saveSharing(p) {
    busy = p.webId; error = null; notice = null;
    try {
      const { added, withdrawn } = changes(p);
      const res = await publish({ webId: p.webId, datasets: { ...choice } });
      notice = !res.ok ? `Changed what ${p.label} gets, but some of it failed — check the cubes under Advanced.`
        : [added.length && `${p.label} now also gets ${names(added)}.`,
          withdrawn.length && `Withdrew ${names(withdrawn)}; their copy is deleted.`,
          !added.length && !withdrawn.length && `Sent ${p.label} an updated copy.`].filter(Boolean).join(' ');
      editing = null;
      reloadId += 1;
    } catch (e) {
      error = e.message;
    } finally {
      busy = null;
    }
  }

  const fmtDate = (iso) => (iso ? new Date(iso).toISOString().slice(0, 16).replace('T', ' ') : '—');
  // Outbound container lives on our own Pod, so present = we publish to them.
  const outStatus = (s) => (s === 200 ? 'they can read it' : `not shared yet (${s || 'no response'})`);
  const inStatus = (items) => {
    if (!items?.length) return 'nothing shared yet';
    const readable = items.filter((item) => item.status === 200).length;
    return `${readable} of ${items.length} readable`;
  };
</script>

<HelpDrawer {...HELP.partners} />

<h1 class="wf2-title wf2-title-level-1">Partners</h1>
<p class="wf2-subtitle">
  Manage who this institution shares with. When you take in a new period, send
  partners an updated copy — that never happens on its own.
</p>

{#if notice}
  <Alert title="Done" variant="success"><p class="wf2-text">{notice}</p></Alert>
{/if}
{#if error}
  <Alert title="Could not update their copy" variant="danger"><p class="mono">{error}</p></Alert>
{/if}

<Async promise={partners} loading="Reading the partner registry from your Pod…">
  {#snippet children(data)}
    {#if data.partners.length === 0}
      <Section title="No partners yet">
        <Panel>
          <p class="wf2-text">You aren't sharing with anyone yet. Add a partner to let them read a dataset's counts.</p>
          <Button variant="primary" to="partner">Add partner</Button>
        </Panel>
      </Section>
    {:else}
      {#each data.partners as p}
        <Section title={p.label}>
          <Panel>
            <DataTable
              columns={['Field', 'Value']}
              rows={[
                ['WebID', p.webId],
                ['Contact', p.contact || '—'],
                ['Datasets you share', (p.outbound.datasets ?? []).map((id) => datasetLabel(datasets, id)).join(', ') || '(none)'],
                ['Counts you share', `${p.outbound.slices?.length ?? 0}`],
                ['Last sent', fmtDate(p.outbound.publishedAt)],
                ['What you share with them', outStatus(p.outbound.status)],
                ['What they share with you', inStatus(p.inbound.containers)],
              ]}
            />
            {#if p.inbound.containers.length}
              <DataTable
                columns={['What they pointed you at', 'Status']}
                rows={p.inbound.containers.map((item) => [
                  item.label ? `${item.label} — ${item.url}` : item.url,
                  item.status === 200 ? 'you can read it' : [401, 403].includes(item.status) ? `they haven't shared it yet (${item.status})` : `can't reach it (${item.status || 'no response'})`,
                ])}
              />
            {/if}
            {#if editing === p.webId}
              {@const { added, withdrawn } = changes(p)}
              <div class="wf2-panel" style="margin-top: var(--space-3)">
                <p class="wf2-text"><strong>What {p.label} gets</strong></p>
                {#each groups as g}
                  <div class="wf2-checkline">
                    <input class="wf2-check" type="checkbox" id="share-{g.id}" bind:checked={choice[g.id]} />
                    <label for="share-{g.id}">{g.label} — {g.slices.length} {g.slices.length === 1 ? 'count' : 'counts'}</label>
                  </div>
                {:else}
                  <p class="wf2-hint">No dataset has counts yet. Add them on the dataset's Sharing tab.</p>
                {/each}
                {#if added.length}<p class="wf2-hint">They will also get a copy of the counts of {names(added)}.</p>{/if}
                {#if withdrawn.length}<p class="wf2-hint">Withdrawing {names(withdrawn)} deletes the copy they have. What they already read can't be taken back.</p>{/if}
                <div class="wf2-row" style="gap:12px">
                  <Button variant="primary" onclick={() => saveSharing(p)} disabled={busy === p.webId}>
                    {busy === p.webId ? 'Saving…' : added.length || withdrawn.length ? 'Save and send' : 'Send an updated copy'}
                  </Button>
                  <Button variant="secondary" onclick={() => { editing = null; }}>Cancel</Button>
                </div>
              </div>
            {:else}
              <div class="wf2-row" style="gap:12px">
                {#if (p.outbound.datasets ?? []).length}
                  <Button variant="primary" onclick={() => updateCubes(p)} disabled={busy === p.webId}>
                    {busy === p.webId ? 'Sending…' : 'Send an updated copy'}
                  </Button>
                {/if}
                <Button variant="secondary" onclick={() => edit(p)} disabled={!groups.length}>Change what you share</Button>
              </div>
            {/if}
          </Panel>
        </Section>
      {/each}
    {/if}

    <div class="wf2-row" style="gap:12px">
      <Button variant="primary" to="partner">Add partner</Button>
    </div>
  {/snippet}
</Async>
